import React, { useEffect, useState } from "react";
import AgentCard from "./Card/AgentCard";
import SimpleDrawer from "../Atoms/Drawers/SimpleDrawer";
import AgentDetails from "./AgentDrawer/AgentDetails";
import NoData from "../NoData/NoData";
import { useNavigate } from "react-router-dom";
import "./css/agent.dashboard.css";
import AgentVersionDropdown from "./AgentDrawer/AgentVersionDropdown";
import { useAgent } from "../../contexts/AgentsContext";
import { useDashboard } from "../../contexts/DashboardContext";
import UploadContent from "../UploadDragger/UploadContent";
import AgentHeader from "./AgentsHeader";
import FooterSpacer from "../Footer/FooterSpacer";
import usePermissions from "../../hooks/userPermissions";

const AgentDashboard = () => {
  const navigate = useNavigate();
  const {
    agents,
    fetchAgents,
    fetchAgentsSilently,
    selectAgent,
    fetchAgentVersions,
    fetchAgentBFs,
    clearSelectedAgent,
    selectedAgent,
    clearAgentVersions,
    clearCurrentAgentVersion,
    agentDetailsDrawerOpen,
    changeAgentDrawerState,
  } = useAgent();

  const { canAgentRead } = usePermissions();

  const { showConfirmationModal, uploadDrawerOpen, changeUploadDrawerState } =
    useDashboard();

  // const [drawerOpen, setDrawerOpen] = useState(false);

  const [searchKey, setSearchKey] = useState("");
  const [statusVal, setStatusVal] = useState("");
  const [providerFilterVal, setProviderFilterVal] = useState("");
  const [functionFilterVal, setFunctionFilterVal] = useState([]);
  const [industryFilterVal, setIndustryFilterVal] = useState("");
  const [sectorFilterVal, setSectorFilterVal] = useState("");

  const [filteredAgents, setFilteredAgents] = useState([]);

  useEffect(() => {
    const getAgentsData = async () => {
      if (agents && agents?.length > 0) {
        await fetchAgentsSilently();
        fetchAgentBFs();
      } else {
        await fetchAgents();
      }
    };

    getAgentsData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filtering logic
  useEffect(() => {
    if (!agents || agents.length === 0) {
      setFilteredAgents([]);
      return;
    }

    let filtered = [...agents];

    // Search filter
    if (searchKey) {
      filtered = filtered.filter(
        (agent) =>
          agent?.agent_name?.toLowerCase().includes(searchKey.toLowerCase()) ||
          agent?.agent_description
            ?.toLowerCase()
            .includes(searchKey.toLowerCase())
      );
    }

    // Status filter
    if (statusVal && statusVal !== "All") {
      filtered = filtered.filter(
        (agent) =>
          agent?.approval_status?.toLowerCase() === statusVal?.toLowerCase()
      );
    }

    // Provider filter
    if (providerFilterVal && providerFilterVal !== "All") {
      filtered = filtered.filter(
        (agent) =>
          agent?.agent_metadata?.a2a?.provider?.toLowerCase() ===
          providerFilterVal.toLowerCase()
      );
    }

    // Industry filter
    if (industryFilterVal && industryFilterVal !== "All") {
      filtered = filtered?.filter((agent) => {
        const industryInfo = agent?.agent_metadata?.a2a?.industry_info;
        if (Array.isArray(industryInfo)) {
          return industryInfo.some(
            (info) =>
              info?.industry?.toLowerCase() === industryFilterVal?.toLowerCase()
          );
        }
        return (
          industryInfo?.industry?.toLowerCase() === industryFilterVal?.toLowerCase()
        );
      });
    }

    // Sector filter
    if (sectorFilterVal && sectorFilterVal !== "All") {
      const lowercaseSector = sectorFilterVal.toLowerCase();
      filtered = filtered.filter((agent) => {
        const industryInfo = agent?.agent_metadata?.a2a?.industry_info;

        if (Array.isArray(industryInfo)) {
          return industryInfo.some(info => {
            if (Array.isArray(info?.sector)) {
              return info.sector.some(sector =>
                sector.toLowerCase() === lowercaseSector
              );
            }
            return false;
          });
        }
        return agent?.agent_metadata?.a2a?.sector?.toLowerCase() === lowercaseSector;
      });
    }

    // Function filter
    if (functionFilterVal && functionFilterVal.length > 0) {
      filtered = filtered.filter((agent) => {
        const businessFunction =
          agent?.business_function ||
          agent?.agent_metadata?.business_function ||
          agent?.category;

        if (businessFunction) {
          const functionStr = String(businessFunction).toLowerCase();
          return functionFilterVal.some((selectedFunction) =>
            functionStr.includes(selectedFunction.toLowerCase())
          );
        }
        return false;
      });
    }

    setFilteredAgents(filtered);
  }, [searchKey, statusVal, providerFilterVal, functionFilterVal, agents, industryFilterVal, sectorFilterVal]);

  const handleResetFilters = () => {
    setSearchKey("");
    setStatusVal("");
    setProviderFilterVal("");
    setFunctionFilterVal([]);
  };

  const handleEdit = (e, card) => {
    e?.stopPropagation();
    showConfirmationModal({
      title: "Edit Agent",
      body: "Are you sure you want to edit this Agent?",
      onOkLabel: "Confirm",
      onOk: () => navigate(`edit-agent/${card.agent_id}`),
    });
  };

  const handleCardClick = (card) => {
    if (canAgentRead) {
      fetchAgentVersions(card?.agent_id);
      selectAgent(card);
      changeAgentDrawerState(true);
    }
  };

  return (
    <div className="dashboard">
      <main className="dashboard-main">
        <section className="dashboard-content">
          <AgentHeader
            setSearchKey={setSearchKey}
            setStatusVal={setStatusVal}
            setProviderFilterVal={setProviderFilterVal}
            setFunctionFilterVal={setFunctionFilterVal}
            handleResetFilters={handleResetFilters}
            setIndustryFilterVal={setIndustryFilterVal}
            setSectorFilterVal={setSectorFilterVal}
          />
          <div>
            <div className="marketPlaceCardsContainer row d-flex">
              {filteredAgents?.map((card, index) => {
                let agentMeta = card?.agent_metadata;
                if (typeof agentMeta === "string") {
                  try {
                    agentMeta = JSON.parse(agentMeta);
                  } catch {
                    agentMeta = {};
                  }
                }
                const a2a = agentMeta?.a2a || {};
                return (
                  <div
                    className="col-12 col-md-4 col-lg-4 col-xl-3"
                    key={`card_${index}`}
                    style={{ padding: "12px" }}
                  >
                    <AgentCard
                      onEdit={(e) => handleEdit(e, card)}
                      onCardClick={() => handleCardClick(card)}
                      agent={card}
                    />
                  </div>
                );
              })}
            </div>
            {(filteredAgents || [])?.length === 0 && (
              <NoData
                containerClsNames={"agent-dashbrd-nodata-cls"}
                main={"Registered Agents"}
              />
            )}
          </div>

          {/* Agent Details drawer */}
          <SimpleDrawer
            drawerOpen={agentDetailsDrawerOpen}
            setDrawerOpen={(open) => {
              changeAgentDrawerState(open);

              if (!open) {
                fetchAgentsSilently();
                fetchAgentBFs();
                clearSelectedAgent();

                clearAgentVersions();
                clearCurrentAgentVersion();
              }
            }}
            placement={"right"}
            closable={false}
            width={"660px"}
            title={<AgentVersionDropdown />}
            padding={{ head: "16", body: "16" }}
            className="mcp-server-drawer"
            seperatorHeaderAndContent={false}
            Component={selectedAgent && <AgentDetails />}
            clsNames="mas-agent-det-drawer"
          />

          {/* Upload Drawer */}
          <SimpleDrawer
            drawerOpen={uploadDrawerOpen}
            setDrawerOpen={(open) => {
              changeUploadDrawerState(open);
              fetchAgentsSilently();
              fetchAgentBFs();
            }}
            placement={"right"}
            closable={false}
            width={"660px"}
            title={"Upload File"}
            padding={{ head: "16", body: "16" }}
            className="mcp-server-drawer"
            seperatorHeaderAndContent={false}
            Component={<UploadContent type={"agent"} />}
            clsNames="mas-agent-det-drawer"
          />
        </section>
      </main>
      <FooterSpacer />
    </div>
  );
};

export default AgentDashboard;
